import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, User } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Users } from "lucide-react";
import { MetricCard } from "../components/comparison/MetricCard";
import { TechniqueComparisonTable } from "../components/comparison/TechniqueComparisonTable";

export default function ParentComparison() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [children, setChildren] = useState<User[]>([]);
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([]);
  const [childrenData, setChildrenData] = useState<Map<string, any>>(new Map());
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    // Parse child IDs from URL
    const childrenParam = searchParams.get("children");
    if (childrenParam) {
      const ids = childrenParam.split(",").filter(Boolean);
      setSelectedChildIds(ids);
      if (ids.length >= 2) {
        loadComparison(ids);
      }
    }
  }, [searchParams]);

  const loadChildren = async () => {
    try {
      setLoading(true);
      const data = await api.getChildren();
      setChildren(data);
    } catch (error) {
      console.error("Failed to load children:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async (childIds: string[]) => {
    try {
      setComparing(true);
      const data = await api.getMultipleChildAnalytics(childIds);
      setChildrenData(data);
    } catch (error) {
      console.error("Failed to load comparison data:", error);
    } finally {
      setComparing(false);
    }
  };

  const toggleChildSelection = (childId: string) => {
    const newSelection = selectedChildIds.includes(childId)
      ? selectedChildIds.filter((id) => id !== childId)
      : [...selectedChildIds, childId];

    setSelectedChildIds(newSelection);

    // Update URL
    if (newSelection.length > 0) {
      setSearchParams({ children: newSelection.join(",") });
    } else {
      setSearchParams({});
    }

    // Load comparison if 2+ selected
    if (newSelection.length >= 2) {
      loadComparison(newSelection);
    }
  };

  const handleDateRangeChange = (days: number) => {
    setDateRange(days);
    if (selectedChildIds.length >= 2) {
      loadComparison(selectedChildIds);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">
          Loading...
        </div>
      </div>
    );
  }

  if (children.length < 2) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="outline"
          onClick={() => navigate("/parent-portal")}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Parent Portal
        </Button>
        <Alert>
          <Users className="h-4 w-4" />
          <AlertDescription>
            You need at least 2 linked children to use the comparison feature.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Prepare entities for ComparisonGrid
  const comparisonEntities = selectedChildIds.map((childId) => {
    const child = children.find((c) => c.id === childId);
    const data = childrenData.get(childId);

    return {
      id: childId,
      name: child?.username || childId,
      data: data?.error ? undefined : data,
      error: data?.error,
      loading: comparing,
    };
  });

  // Prepare metric values
  const getMetricValues = (metricKey: string) => {
    return comparisonEntities
      .filter((e) => e.data && !e.error)
      .map((e) => ({
        entityId: e.id,
        entityName: e.name,
        value: e.data?.[metricKey] ?? 0,
      }));
  };

  // Prepare technique data for comparison table
  const techniqueData = comparisonEntities
    .filter((e) => e.data && !e.error)
    .map((e) => ({
      entityId: e.id,
      entityName: e.name,
      techniques: e.data?.technique_breakdown || [],
    }));

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate("/parent-portal")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Parent Portal
        </Button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Compare Children
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Compare performance metrics across your children
        </p>
      </div>

      {/* Child Selection */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Select Children to Compare</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {children.map((child) => (
              <button
                key={child.id}
                onClick={() => toggleChildSelection(child.id)}
                className={`p-3 rounded-lg border-2 transition-all ${
                  selectedChildIds.includes(child.id)
                    ? "border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                      selectedChildIds.includes(child.id)
                        ? "border-indigo-600 bg-indigo-600"
                        : "border-gray-300"
                    }`}
                  >
                    {selectedChildIds.includes(child.id) && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {child.username}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {selectedChildIds.length > 5 && (
            <Alert className="mt-4" variant="destructive">
              <AlertDescription>
                Maximum 5 children can be compared at once for optimal
                performance. Please deselect some children.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Date Range Filter */}
      {selectedChildIds.length >= 2 && selectedChildIds.length <= 5 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Date Range</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              {[7, 30, 90].map((days) => (
                <Button
                  key={days}
                  variant={dateRange === days ? "default" : "outline"}
                  onClick={() => handleDateRangeChange(days)}
                  disabled={comparing}
                >
                  Last {days} days
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison View */}
      {selectedChildIds.length >= 2 && selectedChildIds.length <= 5 && (
        <>
          {comparing ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-xl text-gray-600 dark:text-gray-400">
                Loading comparison data...
              </div>
            </div>
          ) : (
            <>
              {/* Key Metrics Comparison */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Performance Metrics
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <MetricCard
                    title="Overall Accuracy"
                    values={getMetricValues("overall_accuracy")}
                    unit="%"
                    highlightBest={true}
                    highlightWorst={true}
                  />
                  <MetricCard
                    title="Total Sessions"
                    values={getMetricValues("total_sessions")}
                    highlightBest={true}
                  />
                  <MetricCard
                    title="Problems Correct"
                    values={getMetricValues("total_problems_correct")}
                    highlightBest={true}
                  />
                  <MetricCard
                    title="Average Time per Problem"
                    values={getMetricValues("avg_time_per_problem")}
                    unit="s"
                    highlightBest={true}
                    higherIsBetter={false}
                  />
                  <MetricCard
                    title="Current Streak"
                    values={getMetricValues("current_streak")}
                    unit=" days"
                    highlightBest={true}
                  />
                  <MetricCard
                    title="Techniques Mastered"
                    values={getMetricValues("techniques_mastered")}
                    highlightBest={true}
                  />
                </div>
              </div>

              {/* Technique Comparison Table */}
              {techniqueData.length > 0 && (
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Technique Breakdown
                  </h2>
                  <TechniqueComparisonTable entities={techniqueData} />
                </div>
              )}

              {/* Error Messages */}
              {comparisonEntities.some((e) => e.error) && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>
                    Some children's data could not be loaded. Please try again
                    later.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
